import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import * as Sentry from "@sentry/react";
import {mockTopic} from "../mocks/mock-topic";
import type {
    ChecklistAiContext,
    ChecklistPartElements,
    CollectionDataResponse,
    CollectionLabel,
    CommentsResponse,
    DibDocument,
    DocgenDocumentResponse,
    DocumentPartsResponse,
    GetChecklistPartElementsArgs,
    InlineRelationsResponse,
    PatchXVariableArgs,
    PdfMetadataResponse,
    RawChecklistResponse,
    ResourceConditionsResponse,
    ResourceConditionUserData,
    ResourcePdfTocResponse,
    TopicCollection,
    DocgenTopicData,
} from "../features/content/types";
import {Status} from "../features/content/types";
import {
    findDocgenDataErrors,
    reportDocgenDataErrors,
} from "../features/content/docgen/docgenDataErrors";

type HomeDatabase = {id: string; name: string};

export type GetHomeResponse = {
    filterlist: {
        databases: HomeDatabase[];
        internals?: HomeDatabase[];
    };
    templates: Array<{id: string; name: string}>;
};

export type UserCollectionItem = {
    topic_id: string;
    collection_id: string;
    title: string;
    is_owner: boolean;
    name: string;
    published: boolean;
    lastuse: string;
};

type GetUserCollectionsResponse = {
    items: UserCollectionItem[];
    totalCount: number;
    page: number;
    pageSize: number;
};

export type TopicItemDto = {
    id: string;
    name: string;
    description: string;
    published: string;
    date: string;
    supplier: string;
    type: string;
    resourceTypeId?: number | null;
    language: string;
};

type TopicSetDto = {
    offset: number;
    count: number;
    column: number;
    items: TopicItemDto[];
};

export type UserWorkItemsResponse = {
    history: TopicSetDto;
    favorites: TopicSetDto;
};

export type PortalGrouping = {
    id: number;
    name: string;
    display_order: number;
    products_json: string | null;
    must_include_json: string | null;
    must_exclude_json: string | null;
};

export type Employee = {
    epost: string | null;
    navn: string | null;
    konto_id: number;
    kontonavn: string | null;
};

export type CollaboratorSettledResult = {
    status: "fulfilled" | "rejected";
    data?: {collaboratorKey: string; name: string; email: string};
    error?: string;
};

export type UpdateCollaboratorsResponse = {
    added: CollaboratorSettledResult[];
    removed: CollaboratorSettledResult[];
};

const MOCK_RESOURCE_ID = "mock-resource";

export const dibAppApi = createApi({
    reducerPath: "dibAppApi",
    baseQuery: fetchBaseQuery({baseUrl: "/dibappapi"}),
    tagTypes: [
        "CollectionData",
        "Comments",
        "DocgenConditionData",
        "DocgenDocument",
        "Favorites",
    ],
    endpoints: (builder) => ({
        getHome: builder.query<GetHomeResponse, void>({
            query: () => "/api/home",
            keepUnusedDataFor: 3600,
        }),
        sendToDflow: builder.query<
            void,
            {
                topicId?: string;
                collectionId?: string;
                topics: string;
                folderName?: string | null;
                template: string;
                combine?: boolean;
            }
        >({
            query: ({
                topicId,
                collectionId,
                topics,
                folderName,
                template,
                combine,
            }) => {
                const params = new URLSearchParams();
                if (topicId) params.append("topicId", topicId);
                if (collectionId) params.append("collectionId", collectionId);
                if (topics) params.append("topics", topics);
                if (folderName) params.append("folderName", folderName);
                params.append("template", template);
                params.append("combine", `${!!combine}`);
                return `/api/dib2dflow?${params.toString()}`;
            },
        }),
        getHistory: builder.query<TopicSetDto, void>({
            query: () => "/api/user/history",
            keepUnusedDataFor: 600,
        }),
        getFavorites: builder.query<TopicSetDto, void>({
            query: () => "/api/user/favorites",
            providesTags: ["Favorites"],
            keepUnusedDataFor: 600,
        }),
        getWorkItems: builder.query<UserWorkItemsResponse, void>({
            query: () => "/api/user/work-items",
            providesTags: ["Favorites"], // pin/unpin invalidation refetches both sets
            keepUnusedDataFor: 600,
        }),
        getUserCollections: builder.query<GetUserCollectionsResponse, void>({
            query: () => "/api/user/collections",
            // Membership/label changes only. lastuse-ordering after status,
            // comment or variable edits would need its own narrow tag: those
            // mutations must not refetch the open filling's collection data.
            providesTags: ["CollectionData"],
            keepUnusedDataFor: 600,
        }),
        patchDocumentFollow: builder.mutation<unknown, {topicId: string}>({
            query: ({topicId}) => ({
                url: "/api/documents/follow",
                method: "PATCH",
                params: {topicId},
            }),
            async onQueryStarted({topicId}, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    dibAppApi.util.updateQueryData(
                        "getFavorites",
                        undefined,
                        (draft) => {
                            const exists = draft.items.some(
                                (item) =>
                                    item.id?.toLowerCase() ===
                                    topicId.toLowerCase()
                            );
                            // Only the id is known at call time; consumers of
                            // getFavorites key on id, and the Favorites
                            // invalidation refetch replaces the stub with the
                            // full item.
                            if (!exists) {
                                draft.items.push({id: topicId} as TopicItemDto);
                            }
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: ["Favorites"],
        }),
        deleteDocumentFollow: builder.mutation<unknown, {topicId: string}>({
            query: ({topicId}) => ({
                url: "/api/documents/follow",
                method: "DELETE",
                params: {topicId},
            }),
            async onQueryStarted({topicId}, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    dibAppApi.util.updateQueryData(
                        "getFavorites",
                        undefined,
                        (draft) => {
                            draft.items = draft.items.filter(
                                (item) =>
                                    item.id?.toLowerCase() !==
                                    topicId.toLowerCase()
                            );
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: ["Favorites"],
        }),
        getAllPortals: builder.query<unknown, void>({
            query: () => "/api/portals",
        }),
        getAllPortalsFull: builder.query<unknown, void>({
            query: () => "/api/portals/full",
        }),
        getPortalGroupings: builder.query<PortalGrouping[], void>({
            query: () => "/api/portals/groupings",
            keepUnusedDataFor: 3600,
        }),
        getEmployees: builder.query<Employee[], string>({
            query: (query) => ({
                url: "/api/employees",
                params: query ? {query} : undefined,
            }),
        }),

        getPortalByLanguage: builder.query<unknown, {language: string}>({
            query: ({language}) => `/api/portals/${language}`,
            keepUnusedDataFor: 3600,
        }),

        getResourceTypeId: builder.query<string, {topicId: string}>({
            queryFn: async ({topicId}, _api, _extraOptions, baseQuery) => {
                if (topicId === MOCK_RESOURCE_ID) {
                    return {data: "19"};
                }
                const result = await baseQuery(
                    `/api/topics/${topicId}/resourcetype`
                );
                return result.data !== undefined
                    ? {data: result.data as string}
                    : {error: result.error!};
            },
        }),
        getPdfMetadata: builder.query<PdfMetadataResponse, {resourceId: string}>({
            query: ({resourceId}) =>
                `/api/documents/${encodeURIComponent(resourceId)}/pdf-metadata`,
        }),
        getPdfToc: builder.query<ResourcePdfTocResponse, {resourceId: string}>({
            query: ({resourceId}) =>
                `/api/documents/${encodeURIComponent(resourceId)}/pdf-toc`,
        }),
        getDocument: builder.query<
            DibDocument,
            {resourceId: string; segmentId?: string}
        >({
            queryFn: async (
                {resourceId, segmentId},
                _api,
                _extraOptions,
                baseQuery
            ) => {
                if (resourceId === MOCK_RESOURCE_ID) {
                    return {data: mockTopic as unknown as DibDocument};
                }
                const url = segmentId
                    ? `/api/documents/${resourceId}?segmentId=${encodeURIComponent(segmentId)}`
                    : `/api/documents/${resourceId}`;
                const result = await baseQuery(url);
                if (result.error) return {error: result.error};
                return {data: result.data as DibDocument};
            },
        }),
        getInlineRelations: builder.query<
            InlineRelationsResponse,
            {resourceId: string; id: string}
        >({
            query: ({resourceId, id}) =>
                `/api/documents/${resourceId}/related?id=${encodeURIComponent(id)}`,
        }),
        getDocumentFile: builder.query<unknown, {topicId: string}>({
            query: ({topicId}) => `/api/documents/${topicId}/file`,
        }),
        getDocgenDocument: builder.query<
            DocgenDocumentResponse,
            {resourceId: string; collectionId?: string}
        >({
            query: ({resourceId, collectionId}) => {
                const params = new URLSearchParams();
                if (collectionId) params.set("collectionId", collectionId);
                const qs = params.toString();
                return qs
                    ? `/api/documents/${resourceId}?${qs}`
                    : `/api/documents/${resourceId}`;
            },
            transformResponse: (
                topicData: DocgenTopicData
            ): DocgenDocumentResponse => ({
                // The autoCount → dib-x-section__children restructuring that
                // used to happen here now lives in @dibkunnskap/dokumentgenerator
                // (normalizeDocument); the raw document is passed through.
                topicData,
                metadata: {
                    topicId: topicData.topicId,
                    id: topicData.id,
                    name: topicData.name,
                    resourceTypeId: topicData.resourceTypeId,
                },
                // Generator elements without their object (e.g. the two *N*
                // spans in 49241c44, box#1794) would crash the generator. A
                // payload without access has no docgen objects by design
                // (#437), so it isn't checked.
                dataErrors: topicData.access
                    ? findDocgenDataErrors(topicData)
                    : [],
            }),
            // Once per fetch, not per render or cache hit.
            async onQueryStarted(_args, {queryFulfilled}) {
                // A failed fetch is not a data error; consumers show their
                // own error state for it.
                const result = await queryFulfilled.catch(() => null);
                if (result) {
                    reportDocgenDataErrors(
                        result.data.topicData,
                        result.data.dataErrors
                    );
                }
            },
            providesTags: ["DocgenDocument"],
        }),
        autofillDocumentForOrgNr: builder.query<
            void,
            {
                orgnr: string;
                variableId: string;
                triggerName: string;
                documentIds: {topicId: string; collectionId?: string};
            }
        >({
            query: ({orgnr, triggerName, documentIds}) => {
                const params = new URLSearchParams();
                params.set("orgId", orgnr);
                params.set("triggerId", triggerName);
                if (documentIds.collectionId) {
                    params.set("collectionId", documentIds.collectionId);
                }
                return `/api/documents/${documentIds.topicId}/companydata?${params.toString()}`;
            },
            async onQueryStarted(_args, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(dibAppApi.util.invalidateTags(["DocgenDocument"]));
                } catch (err) {
                    console.error("Could not autofill document:", err);
                    Sentry.captureException(err, {
                        tags: {feature: "docs.autofill"},
                    });
                }
            },
        }),
        getResourceConditions: builder.query<
            ResourceConditionsResponse,
            {resourceId: string}
        >({
            query: ({resourceId}) => `/api/documents/${resourceId}/conditions`,
        }),
        getResourceConditionUserData: builder.query<
            ResourceConditionUserData,
            {topicId: string; collectionId?: string}
        >({
            query: ({topicId, collectionId}) =>
                `/api/condition-label-data/${encodeURIComponent(topicId)}/${encodeURIComponent(collectionId ?? "")}`,
            // The backend serialises this DTO with snake_case names
            // (ConditionLabelSelectionDto: topic_id, collection_id,
            // label_group, label), which is also the shape dokumentgenerator
            // takes. Only the nullable fields are normalised.
            transformResponse: (
                response: {
                    topic_id: string | null;
                    collection_id: string | null;
                    label_group: string | null;
                    label: string | null;
                }[]
            ): ResourceConditionUserData =>
                response.map((item) => ({
                    topic_id: item.topic_id ?? "",
                    collection_id: item.collection_id ?? "",
                    label_group: item.label_group ?? "",
                    label: item.label ?? "",
                })),
            providesTags: ["DocgenConditionData"],
        }),
        setResourceCondition: builder.mutation<
            void,
            {
                documentIds: {topicId: string; collectionId?: string};
                conditionLabelGroup: string;
                conditionLabel: string;
            }
        >({
            query: ({documentIds, conditionLabelGroup, conditionLabel}) => ({
                url: "/api/condition-label-data",
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    topicId: documentIds.topicId,
                    collectionId: documentIds.collectionId ?? null,
                    labelGroup: conditionLabelGroup,
                    label: conditionLabel,
                },
            }),
            async onQueryStarted(
                {documentIds, conditionLabelGroup, conditionLabel},
                {dispatch, queryFulfilled}
            ) {
                const patchResult = dispatch(
                    dibAppApi.util.updateQueryData(
                        "getResourceConditionUserData",
                        {
                            topicId: documentIds.topicId,
                            collectionId: documentIds.collectionId,
                        },
                        (draft) => {
                            const idx = draft.findIndex(
                                (item) =>
                                    item.label_group === conditionLabelGroup
                            );
                            const entry = {
                                topic_id: documentIds.topicId,
                                collection_id: documentIds.collectionId ?? "",
                                label_group: conditionLabelGroup,
                                label: conditionLabel,
                            };
                            if (idx >= 0) {
                                draft[idx] = entry;
                            } else {
                                draft.push(entry);
                            }
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        getChecklist: builder.query<
            RawChecklistResponse,
            {topicId: string; isPreview?: boolean}
        >({
            query: ({topicId, isPreview = false}) =>
                isPreview
                    ? `/api/checklists/${topicId}?cache=false`
                    : `/api/checklists/${topicId}`,
        }),

        getTopicCollections: builder.query<
            TopicCollection[],
            {topicId: string}
        >({
            query: ({topicId}) =>
                `/api/collections?topicId=${topicId}&pageSize=500`,
            transformResponse: (response: {
                objects: Record<string, TopicCollection>;
            }) => {
                try {
                    return Object.values(response.objects).filter(
                        (c) => c.data.draft === 0
                    );
                } catch {
                    return [];
                }
            },
            providesTags: ["CollectionData"],
        }),
        getCollectionData: builder.query<unknown, {topicId: string}>({
            query: ({topicId}) =>
                `/api/collectiondata/collection?topicId=${topicId}`,
        }),

        exportTopic: builder.query<unknown, {topicId: string; format: string}>({
            query: ({topicId, format}) =>
                `/api/topics/${topicId}/export/${format}`,
        }),

        getChecklistCollectionData: builder.query<
            CollectionDataResponse,
            {topicId: string; resourceId: string; collectionId: string}
        >({
            query: ({topicId, resourceId, collectionId}) => {
                const params = new URLSearchParams();
                params.set("topicId", topicId);
                params.set("resourceId", resourceId);
                return `/api/collections/${collectionId}?${params.toString()}`;
            },
            providesTags: ["CollectionData"],
        }),
        patchStatusForCheckItems: builder.mutation<
            void,
            {
                topicId: string;
                resourceId: string;
                collectionId: string;
                itemIds: string[];
                status: Status;
            }
        >({
            query: ({topicId, collectionId, itemIds, status}) => ({
                url: `/api/collections/${collectionId}/checkitems`,
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {topicId, itemIds, status},
            }),
            async onQueryStarted(
                {topicId, resourceId, collectionId, itemIds, status},
                {dispatch, queryFulfilled}
            ) {
                const patchResult = dispatch(
                    dibAppApi.util.updateQueryData(
                        "getChecklistCollectionData",
                        {topicId, resourceId, collectionId},
                        (draft) => {
                            for (const id of itemIds) {
                                draft.checkitems[id] = {
                                    ...(draft.checkitems[id] ?? {
                                        collectionId,
                                        id,
                                    }),
                                    status,
                                };
                            }
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        patchDimensions: builder.mutation<
            void,
            {
                topicId: string;
                resourceId: string;
                collectionId: string;
                dimensions: string[];
                labels: CollectionLabel[];
            }
        >({
            query: ({topicId, collectionId, dimensions}) => ({
                url: `/api/collections/${collectionId}/dimensions`,
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {topicId, dimensions},
            }),
            async onQueryStarted(args, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    dibAppApi.util.updateQueryData(
                        "getChecklistCollectionData",
                        {
                            topicId: args.topicId,
                            resourceId: args.resourceId,
                            collectionId: args.collectionId,
                        },
                        (draft) => {
                            draft.labels = args.labels;
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        patchCollectionName: builder.mutation<
            void,
            {
                topicId: string;
                resourceId: string;
                collectionId: string;
                name: string;
            }
        >({
            query: ({collectionId, name}) => ({
                url: `/api/collections/${collectionId}/name`,
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {name},
            }),
            async onQueryStarted(args, {dispatch, queryFulfilled}) {
                const trimmed = args.name.trim();
                const patchResult = dispatch(
                    dibAppApi.util.updateQueryData(
                        "getChecklistCollectionData",
                        {
                            topicId: args.topicId,
                            resourceId: args.resourceId,
                            collectionId: args.collectionId,
                        },
                        (draft) => {
                            if (draft.metadata) draft.metadata.name = trimmed;
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: ["CollectionData"],
        }),
        patchDocumentSelected: builder.mutation<
            void,
            {
                topicId: string;
                resourceId: string;
                collectionId: string;
                documentId: string;
                selected: boolean;
            }
        >({
            query: ({collectionId, documentId, selected}) => ({
                url: `/api/collections/${collectionId}/topics/${documentId}`,
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {selected},
            }),
            async onQueryStarted(
                {topicId, resourceId, collectionId, documentId, selected},
                {dispatch, queryFulfilled}
            ) {
                const patchResult = dispatch(
                    dibAppApi.util.updateQueryData(
                        "getChecklistCollectionData",
                        {topicId, resourceId, collectionId},
                        (draft) => {
                            if (!selected) {
                                draft.documents = draft.documents.filter(
                                    (doc) => doc.topicId !== documentId
                                );
                            } else {
                                draft.documents.push({
                                    id: documentId,
                                    topicId: documentId,
                                    selected: true,
                                });
                            }
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        deleteFilling: builder.mutation<void, {collectionId: string}>({
            query: ({collectionId}) => ({
                url: `/api/collections/${collectionId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["CollectionData"],
        }),
        updateCollectionCollaborators: builder.mutation<
            UpdateCollaboratorsResponse,
            {
                collectionId: string;
                topicId: string;
                toAdd: {email: string; name: string}[];
                toRemove: {id: string}[];
            }
        >({
            query: ({collectionId, topicId, toAdd, toRemove}) => ({
                url: `/api/collections/${collectionId}/collaborators`,
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: {topicId, toAdd, toRemove},
            }),
            invalidatesTags: ["CollectionData"],
        }),
        getComments: builder.query<CommentsResponse, {collectionId: string}>({
            query: ({collectionId}) =>
                `/api/collections/${collectionId}/comments`,
            transformResponse: (response: CommentsResponse) =>
                response ?? {elementdata: {}, objects: {}},
            providesTags: ["Comments"],
        }),
        addComment: builder.mutation<
            void,
            {topicId: string; collectionId: string; id: string; comment: string}
        >({
            query: ({collectionId, topicId, id, comment}) => ({
                url: `/api/collections/${collectionId}/comments`,
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: {id, comment, topicId},
            }),
            invalidatesTags: ["Comments"],
        }),
        updateComment: builder.mutation<
            void,
            {
                collectionId: string;
                commentId: string;
                comment: string;
            }
        >({
            query: ({collectionId, commentId, comment}) => ({
                url: `/api/collections/${collectionId}/comments/${commentId}`,
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {comment},
            }),
            invalidatesTags: ["Comments"],
        }),
        deleteComment: builder.mutation<
            void,
            {collectionId: string; commentId: string}
        >({
            query: ({collectionId, commentId}) => ({
                url: `/api/collections/${collectionId}/comments/${commentId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Comments"],
        }),
        resetXVariables: builder.mutation<
            void,
            {topicId: string; collectionId?: string}
        >({
            query: ({topicId, collectionId}) => {
                const params = new URLSearchParams();
                params.set("topicId", topicId);
                return {
                    url: collectionId
                        ? `/api/collections/${collectionId}/variables?${params.toString()}`
                        : `/api/variables?${params.toString()}`,
                    method: "DELETE",
                };
            },
            invalidatesTags: ["DocgenDocument"],
        }),
        patchXVariable: builder.mutation<void, PatchXVariableArgs>({
            query: ({topicId, collectionId, change}) => ({
                url: collectionId
                    ? `/api/collections/${collectionId}/variables/${change.id}`
                    : `/api/variables/${change.id}`,
                method: "PATCH",
                headers: {"Content-Type": "application/json"},
                body: {
                    topicId,
                    type: change.type,
                    value: change.value,
                    n: change.type === "dib-x-var" ? change.n : 0,
                },
            }),
        }),
        resetConditionalUserData: builder.mutation<
            void,
            {topicId: string; collectionId?: string}
        >({
            query: ({topicId, collectionId}) => ({
                url: "/api/condition-label-data",
                method: "DELETE",
                headers: {"Content-Type": "application/json"},
                body: {
                    topicId,
                    collectionId: collectionId || null,
                },
            }),
            invalidatesTags: ["DocgenConditionData"],
        }),
        createCollection: builder.mutation<
            {collectionId: string; name: string},
            {topicId: string; name: string; dimensions?: string[]}
        >({
            query: ({topicId, name, dimensions}) => ({
                url: "/api/collections",
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: {topicId, name, dimensions: dimensions ?? []},
            }),
            invalidatesTags: ["CollectionData"],
        }),
        duplicateCollection: builder.mutation<
            {collectionId: string; name: string},
            {
                collectionId: string;
                name: string;
                ok: boolean;
                na: boolean;
                deviation: boolean;
                comment: boolean;
                variables: boolean;
            }
        >({
            query: ({collectionId, name, ok, na, deviation, comment, variables}) => ({
                url: `/api/collections/${collectionId}/duplicate`,
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: {name, ok, na, deviation, comment, variables},
            }),
            invalidatesTags: ["CollectionData"],
        }),

        getChecklistPartElements: builder.query<
            ChecklistPartElements,
            GetChecklistPartElementsArgs
        >({
            query: ({resourceId, id, recursive = false, isPreview = false}) => {
                const params = new URLSearchParams();
                params.set("Id", id);
                if (recursive) params.set("Recursive", "true");
                if (isPreview) params.set("Cache", "false");
                return `/api/checklists/${resourceId}/elements?${params.toString()}`;
            },
        }),
        /** Resource id or topic id (GUID); an exact resource-id match wins. Not cached server-side. */
        getChecklistAiContext: builder.query<
            ChecklistAiContext,
            {resourceId: string}
        >({
            query: ({resourceId}) => `/api/checklists/${resourceId}/ai-context`,
        }),
        getDocumentParts: builder.query<
            DocumentPartsResponse,
            {resourceId: string; segmentId: string; id: string}
        >({
            query: ({resourceId, segmentId, id}) =>
                `/api/documents/${resourceId}/parts?segmentId=${segmentId}&id=${id}`,
            // Long enough for a prefetched excerpt to survive until the user hovers.
            keepUnusedDataFor: 600,
        }),
    }),
});
